import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useNotifications } from '../context/NotificationContext';
import { formatRelativeTime } from '../utils/time';

export default function NotificationBell() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotificationClick = (n) => {
    markAsRead(n.id);
    if (n.link) navigate(n.link);
    setOpen(false);
  };

  const recent = notifications.slice(0, 6);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="relative w-8 h-8 flex items-center justify-center text-ink-500 hover:text-ink-900 transition-colors duration-200"
        aria-label="Notifications"
      >
        <Bell size={18} strokeWidth={1.75} />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 w-4 h-4 bg-terra-400 text-white text-[9px] font-bold rounded-full flex items-center justify-center translate-x-1 -translate-y-1 leading-none">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-sand-200 rounded-xl shadow-lg z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-sand-200">
            <h3 className="text-sm font-semibold text-ink-900">Notifications</h3>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                className="text-xs text-terra-400 hover:text-terra-500 font-medium transition-colors duration-200"
              >
                Mark all as read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-72 overflow-y-auto divide-y divide-sand-100">
            {recent.length === 0 ? (
              <div className="px-4 py-10 text-center">
                <Bell size={24} strokeWidth={1.5} className="text-sand-300 mx-auto mb-2" />
                <p className="text-sm text-ink-400">No notifications yet</p>
              </div>
            ) : (
              recent.map((n) => (
                <button
                  key={n.id}
                  onClick={() => handleNotificationClick(n)}
                  className={`w-full text-left px-4 py-3 flex items-start gap-2.5 hover:bg-sand-50 transition-colors duration-150 ${!n.is_read ? 'bg-terra-50/50' : ''}`}
                >
                  <span
                    className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${!n.is_read ? 'bg-terra-400' : 'bg-transparent'}`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-ink-800 truncate">{n.title}</p>
                    <p className="text-xs text-ink-500 mt-0.5 line-clamp-2 leading-relaxed">{n.body}</p>
                    <p className="text-xs text-ink-400 mt-1">{formatRelativeTime(n.created_at)}</p>
                  </div>
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="px-4 py-2.5 border-t border-sand-200 bg-sand-50">
            <Link
              to="/notifications"
              onClick={() => setOpen(false)}
              className="text-xs text-terra-400 hover:text-terra-500 font-medium transition-colors duration-200"
            >
              View all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
